import { NURSE_STAFF_BASIC_TRAINING_ITEMS } from "./nurseStaffConstants";
import { getLosAngelesDateKey } from "../utils/losAngelesDateTime";

export type StaffDelegationRequirements = {
  dateOfBirth: string;
  credentialType: string;
  credentialNumber: string;
  credentialIssueDate: string;
  credentialExpiryDate: string;
  coreTrainingCompleted: boolean;
  coreTrainingDate: string;
  diabetesTrainingCompleted: boolean;
  diabetesTrainingDate: string;
  narBasicTrainingCompleted: boolean;
  narBasicTrainingDate: string;
  basicTrainingCoreCompetencyCompleted: boolean;
  basicTrainingCoreCompetencyDate: string;
  basicTraining75HoursCompleted: boolean;
  basicTraining75HoursDate: string;
  fundamentalsOfCareCompleted: boolean;
  fundamentalsOfCareDate: string;
  revisedFundamentalsOfCareCompleted: boolean;
  revisedFundamentalsOfCareDate: string;
  dda32HourLetterCompleted: boolean;
  dda32HourLetterDate: string;
  exemptLtcwCompleted: boolean;
  exemptLtcwDate: string;
};

export const EMPTY_STAFF_DELEGATION_REQUIREMENTS: StaffDelegationRequirements = {
  dateOfBirth: "",
  credentialType: "",
  credentialNumber: "",
  credentialIssueDate: "",
  credentialExpiryDate: "",
  coreTrainingCompleted: false,
  coreTrainingDate: "",
  diabetesTrainingCompleted: false,
  diabetesTrainingDate: "",
  narBasicTrainingCompleted: false,
  narBasicTrainingDate: "",
  basicTrainingCoreCompetencyCompleted: false,
  basicTrainingCoreCompetencyDate: "",
  basicTraining75HoursCompleted: false,
  basicTraining75HoursDate: "",
  fundamentalsOfCareCompleted: false,
  fundamentalsOfCareDate: "",
  revisedFundamentalsOfCareCompleted: false,
  revisedFundamentalsOfCareDate: "",
  dda32HourLetterCompleted: false,
  dda32HourLetterDate: "",
  exemptLtcwCompleted: false,
  exemptLtcwDate: "",
};

export const createEmptyStaffDelegationRequirements = (): StaffDelegationRequirements => ({
  ...EMPTY_STAFF_DELEGATION_REQUIREMENTS,
});

export const normalizeStaffDelegationRequirements = (value: any): StaffDelegationRequirements => ({
  ...EMPTY_STAFF_DELEGATION_REQUIREMENTS,
  ...(value || {}),
  ...Object.fromEntries(NURSE_STAFF_BASIC_TRAINING_ITEMS.flatMap((item) => [
    [item.completedKey, value?.[item.completedKey] === true],
    [item.dateKey, String(value?.[item.dateKey] || "")],
  ])),
  coreTrainingCompleted: value?.coreTrainingCompleted === true,
  diabetesTrainingCompleted: value?.diabetesTrainingCompleted === true,
  exemptLtcwCompleted: value?.exemptLtcwCompleted === true,
});

export const getStaffDelegationRequirementsValidationMessage = (value: any): string => {
  const current = normalizeStaffDelegationRequirements(value);
  const today = getLosAngelesDateKey(new Date());
  const dateFields = [
    ["coreTrainingCompleted", "coreTrainingDate", "9-hour Nurse Delegation Core Training"],
    ["diabetesTrainingCompleted", "diabetesTrainingDate", "3-hour Nurse Delegation Special Focus on Diabetes"],
    ["narBasicTrainingCompleted", "narBasicTrainingDate", "NAR basic training"],
    ...NURSE_STAFF_BASIC_TRAINING_ITEMS.map((item) => [item.completedKey, item.dateKey, item.label]),
    ["exemptLtcwCompleted", "exemptLtcwDate", "Exempt Long-Term Care Worker verification"],
  ] as const;
  for (const [completedKey, dateKey, label] of dateFields) {
    const date = String(current[dateKey] || "").trim();
    if (current[completedKey] === true && !date) return `${label} requires a completion date.`;
    if (date && date > today) return `${label} date must be today or earlier.`;
  }
  if (current.credentialIssueDate && current.credentialIssueDate > today) return "Credential issue date must be today or earlier.";
  if (current.credentialExpiryDate && current.credentialExpiryDate < today) return "Credential expiry date must be today or later.";
  return "";
};

